import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ContentCopy, ContentPaste } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import TabIcon from '@mui/icons-material/Tab';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ComponentEditor from './ComponentEditor';
import { availableComponents } from '../../../pages/dynamic-page';
import {
  ContainerConfig,
  ComponentConfig,
  TabConfig,
} from '../../../pages/types';

interface ContainerEditorProps {
  container: ContainerConfig;
  onChange: (updatedContainer: ContainerConfig) => void;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}

const ContainerEditor: React.FC<ContainerEditorProps> = ({
  container,
  onChange,
  configParam,
  handleUpload,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hasClipboardData, setHasClipboardData] = useState(false);

  // Check clipboard on component mount and periodically
  const checkClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const data = JSON.parse(clipboardText);
      setHasClipboardData(!!data?.type);
    } catch {
      setHasClipboardData(false);
    }
  };

  // Check clipboard periodically
  React.useEffect(() => {
    checkClipboard();
    const interval = setInterval(checkClipboard, 1000);
    return () => clearInterval(interval);
  }, []);

  // Initialize container with defaults if needed
  const safeContainer = {
    columns: container?.columns || 2,
    spacing: container?.spacing || 2,
    maxWidth: container?.maxWidth || '1100px',
    minColumns: container?.minColumns || undefined,
    items: container?.items || [],
    useTabs: container?.useTabs || false,
    tabs: container?.tabs || [],
  };

  // Handle configuration changes
  const handleConfigChange = (key: keyof ContainerConfig, value: any) => {
    onChange({
      ...safeContainer,
      [key]: value,
    });
  };

  // Handle child component changes
  const handleItemChange = (
    index: number,
    updatedComponent: ComponentConfig
  ) => {
    const updatedItems = [...safeContainer.items];
    updatedItems[index] = updatedComponent;
    handleConfigChange('items', updatedItems);
  };

  // Add new item to container
  const handleAddItem = (type: string) => {
    const newComponent: ComponentConfig = {
      type,
      props: {},
      styles: {},
    };
    handleConfigChange('items', [...safeContainer.items, newComponent]);
  };

  // Remove item from container
  const handleRemoveItem = (index: number) => {
    const updatedItems = safeContainer.items.filter((_, i) => i !== index);
    handleConfigChange('items', updatedItems);
  };

  // Add function to move items up or down
  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const items = [...safeContainer.items];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap the items
    [items[index], items[newIndex]] = [items[newIndex], items[index]];

    handleConfigChange('items', items);
  };

  // Copy/Paste functionality
  const handleCopyComponent = async (index: number) => {
    const componentToCopy = safeContainer.items[index];
    const copiedComponent = {
      ...componentToCopy,
      props: { ...componentToCopy.props },
      styles: { ...componentToCopy.styles },
    };

    // Copy to system clipboard as JSON for sharing between root level and containers
    try {
      const componentJson = JSON.stringify(copiedComponent, null, 2);
      await navigator.clipboard.writeText(componentJson);
      console.log('Component copied to clipboard');
      setHasClipboardData(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handlePasteComponent = async (insertIndex: number) => {
    // Get component from system clipboard
    try {
      const clipboardText = await navigator.clipboard.readText();
      const componentToPaste = JSON.parse(clipboardText);

      // Validate that it's a component structure
      if (!componentToPaste?.type) {
        throw new Error('Invalid component structure in clipboard');
      }

      const updatedItems = [...safeContainer.items];
      updatedItems.splice(insertIndex, 0, componentToPaste);
      handleConfigChange('items', updatedItems);
      console.log('Component pasted from clipboard');
    } catch (err) {
      console.error('Failed to paste from clipboard:', err);
    }
  }; // Copy/Paste for Tab Items
  const handleCopyTabComponent = async (
    tabIndex: number,
    itemIndex: number
  ) => {
    const componentToCopy = safeContainer.tabs[tabIndex].items[itemIndex];
    const copiedComponent = {
      ...componentToCopy,
      props: { ...componentToCopy.props },
      styles: { ...componentToCopy.styles },
    };

    // Also copy to system clipboard as JSON
    try {
      const componentJson = JSON.stringify(copiedComponent, null, 2);
      await navigator.clipboard.writeText(componentJson);
      console.log('Tab component copied to clipboard');
      setHasClipboardData(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handlePasteTabComponent = async (
    tabIndex: number,
    insertIndex: number
  ) => {
    // Get component from system clipboard
    try {
      const clipboardText = await navigator.clipboard.readText();
      const componentToPaste = JSON.parse(clipboardText);

      // Validate that it's a component structure
      if (!componentToPaste?.type) {
        throw new Error('Invalid component structure in clipboard');
      }

      const updatedTabs = [...safeContainer.tabs];
      updatedTabs[tabIndex].items.splice(insertIndex, 0, componentToPaste);
      handleConfigChange('tabs', updatedTabs);
      console.log('Component pasted from clipboard');
    } catch (err) {
      console.error('Failed to paste from clipboard:', err);
    }
  }; // Duplicate functionality
  const handleDuplicateComponent = (index: number) => {
    const componentToDuplicate = safeContainer.items[index];
    const duplicatedComponent = {
      ...componentToDuplicate,
      props: { ...componentToDuplicate.props },
      styles: { ...componentToDuplicate.styles },
    };

    const updatedItems = [...safeContainer.items];
    updatedItems.splice(index + 1, 0, duplicatedComponent);
    handleConfigChange('items', updatedItems);
  };

  const handleDuplicateTabComponent = (tabIndex: number, itemIndex: number) => {
    const componentToDuplicate = safeContainer.tabs[tabIndex].items[itemIndex];
    const duplicatedComponent = {
      ...componentToDuplicate,
      props: { ...componentToDuplicate.props },
      styles: { ...componentToDuplicate.styles },
    };

    const updatedTabs = [...safeContainer.tabs];
    updatedTabs[tabIndex].items.splice(itemIndex + 1, 0, duplicatedComponent);
    handleConfigChange('tabs', updatedTabs);
  };

  // Tab handling functions
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  const handleAddTab = () => {
    const newTab: TabConfig = {
      label: `Tab ${safeContainer.tabs.length + 1}`,
      items: [],
    };
    handleConfigChange('tabs', [...safeContainer.tabs, newTab]);
    setActiveTabIndex(safeContainer.tabs.length); // Switch to new tab
  };

  const handleRemoveTab = (tabIndex: number) => {
    const updatedTabs = safeContainer.tabs.filter((_, i) => i !== tabIndex);
    handleConfigChange('tabs', updatedTabs);
    // Adjust active tab if necessary
    if (activeTabIndex >= updatedTabs.length && updatedTabs.length > 0) {
      setActiveTabIndex(updatedTabs.length - 1);
    } else if (updatedTabs.length === 0) {
      setActiveTabIndex(0);
    }
  };

  const handleUpdateTabLabel = (tabIndex: number, newLabel: string) => {
    const updatedTabs = [...safeContainer.tabs];
    updatedTabs[tabIndex] = { ...updatedTabs[tabIndex], label: newLabel };
    handleConfigChange('tabs', updatedTabs);
  };

  const handleAddItemToTab = (tabIndex: number, type: string) => {
    const newComponent: ComponentConfig = {
      type,
      props: {},
      styles: {},
    };
    const updatedTabs = [...safeContainer.tabs];
    updatedTabs[tabIndex].items = [
      ...updatedTabs[tabIndex].items,
      newComponent,
    ];
    handleConfigChange('tabs', updatedTabs);
  };

  const handleRemoveItemFromTab = (tabIndex: number, itemIndex: number) => {
    const updatedTabs = [...safeContainer.tabs];
    updatedTabs[tabIndex].items = updatedTabs[tabIndex].items.filter(
      (_, i) => i !== itemIndex
    );
    handleConfigChange('tabs', updatedTabs);
  };

  const handleUpdateTabItem = (
    tabIndex: number,
    itemIndex: number,
    updatedComponent: ComponentConfig
  ) => {
    const updatedTabs = [...safeContainer.tabs];
    updatedTabs[tabIndex].items[itemIndex] = updatedComponent;
    handleConfigChange('tabs', updatedTabs);
  };

  const handleMoveTabItem = (
    tabIndex: number,
    itemIndex: number,
    direction: 'up' | 'down'
  ) => {
    const updatedTabs = [...safeContainer.tabs];
    const items = [...updatedTabs[tabIndex].items];
    const newIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;

    if (newIndex < 0 || newIndex >= items.length) return;

    [items[itemIndex], items[newIndex]] = [items[newIndex], items[itemIndex]];
    updatedTabs[tabIndex].items = items;
    handleConfigChange('tabs', updatedTabs);
  };

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        border: '4px dashed #ffffff',
        p: 2,
        borderRadius: 1,
        backgroundColor: '#f0f0f0',
      }}
    >
      <Typography variant="h6">Container Settings</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Columns"
          type="number"
          InputProps={{ inputProps: { min: 1, max: 6 } }}
          value={safeContainer.columns}
          onChange={(e) =>
            handleConfigChange('columns', Number(e.target.value))
          }
          sx={{ width: '120px' }}
        />
        <Tooltip
          title="Forces minimum columns even on mobile"
          children={
            <TextField
              label="Min Columns"
              type="number"
              InputProps={{ inputProps: { min: 1, max: 6 } }}
              value={safeContainer.minColumns || ''}
              onChange={(e) =>
                handleConfigChange(
                  'minColumns',
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="Override mobile"
              sx={{ width: '150px' }}
            />
          }
        />

        <TextField
          label="Spacing"
          type="number"
          InputProps={{ inputProps: { min: 0, max: 10 } }}
          value={safeContainer.spacing}
          onChange={(e) =>
            handleConfigChange('spacing', Number(e.target.value))
          }
          sx={{ width: '120px' }}
        />
        <TextField
          label="Max Width"
          value={safeContainer.maxWidth}
          onChange={(e) => handleConfigChange('maxWidth', e.target.value)}
          placeholder="e.g., 1200px, 100%, 80rem"
          sx={{ width: '180px' }}
        />
      </Box>

      {/* Tabs Configuration */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TabIcon />
          <Typography variant="h6">Tab Configuration</Typography>
          <Switch
            checked={safeContainer.useTabs}
            onChange={(e) => {
              handleConfigChange('useTabs', e.target.checked);
              if (!e.target.checked) {
                setActiveTabIndex(0); // Reset to first tab when disabled
              }
            }}
            color="primary"
          />
          <Typography variant="body2">
            {safeContainer.useTabs ? 'Use Tabs' : 'Use Grid Layout'}
          </Typography>
        </Box>

        <Collapse in={safeContainer.useTabs}>
          <Box sx={{ mb: 2 }}>
            {/* Tab Management Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddTab}
                size="small"
              >
                Add Tab
              </Button>
              {safeContainer.tabs.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {safeContainer.tabs.length} tab
                  {safeContainer.tabs.length !== 1 ? 's' : ''} configured
                </Typography>
              )}
            </Box>

            {/* Material-UI Tabs Interface */}
            {safeContainer.tabs.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {/* Tab Headers */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={Math.min(
                      activeTabIndex,
                      safeContainer.tabs.length - 1
                    )}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: '48px' }}
                  >
                    {safeContainer.tabs.map((tab, tabIndex) => (
                      <Tab
                        key={tabIndex}
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <span>{tab.label}</span>
                            <Box
                              component="span"
                              sx={{
                                fontSize: '0.75rem',
                                background: 'rgba(0,0,0,0.1)',
                                borderRadius: '50%',
                                minWidth: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {tab.items.length}
                            </Box>
                          </Box>
                        }
                        sx={{
                          minHeight: '48px',
                          textTransform: 'none',
                        }}
                      />
                    ))}
                  </Tabs>
                </Box>

                {/* Active Tab Content */}
                {safeContainer.tabs[
                  Math.min(activeTabIndex, safeContainer.tabs.length - 1)
                ] && (
                  <Box>
                    {/* Tab Label Editor */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                      }}
                    >
                      <TextField
                        label="Tab Label"
                        value={
                          safeContainer.tabs[
                            Math.min(
                              activeTabIndex,
                              safeContainer.tabs.length - 1
                            )
                          ].label
                        }
                        onChange={(e) =>
                          handleUpdateTabLabel(
                            Math.min(
                              activeTabIndex,
                              safeContainer.tabs.length - 1
                            ),
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        aria-label="delete tab"
                        onClick={() =>
                          handleRemoveTab(
                            Math.min(
                              activeTabIndex,
                              safeContainer.tabs.length - 1
                            )
                          )
                        }
                        color="error"
                        title="Delete this tab"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    {/* Tab Items */}
                    <Typography
                      variant="body1"
                      sx={{ mb: 2, fontWeight: 'medium' }}
                    >
                      Tab Content (
                      {
                        safeContainer.tabs[
                          Math.min(
                            activeTabIndex,
                            safeContainer.tabs.length - 1
                          )
                        ].items.length
                      }{' '}
                      items)
                    </Typography>

                    {/* Paste button at the top of tab items */}
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentPaste />}
                        onClick={() =>
                          handlePasteTabComponent(
                            Math.min(
                              activeTabIndex,
                              safeContainer.tabs.length - 1
                            ),
                            0
                          )
                        }
                        disabled={!hasClipboardData}
                        sx={{ opacity: hasClipboardData ? 1 : 0.3 }}
                      >
                        Paste Component Here
                      </Button>
                    </Box>

                    {safeContainer.tabs[
                      Math.min(activeTabIndex, safeContainer.tabs.length - 1)
                    ].items.map((item, itemIndex) => (
                      <React.Fragment key={itemIndex}>
                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            backgroundColor: '#fff',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {item.type || 'Component'} #{itemIndex + 1}
                            </Typography>
                            <IconButton
                              aria-label="move up"
                              onClick={() =>
                                handleMoveTabItem(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex,
                                  'up'
                                )
                              }
                              disabled={itemIndex === 0}
                              size="small"
                            >
                              <ArrowUpwardIcon />
                            </IconButton>
                            <IconButton
                              aria-label="move down"
                              onClick={() =>
                                handleMoveTabItem(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex,
                                  'down'
                                )
                              }
                              disabled={
                                itemIndex ===
                                safeContainer.tabs[
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  )
                                ].items.length -
                                  1
                              }
                              size="small"
                            >
                              <ArrowDownwardIcon />
                            </IconButton>
                            <IconButton
                              aria-label="duplicate"
                              onClick={() =>
                                handleDuplicateTabComponent(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex
                                )
                              }
                              color="primary"
                              size="small"
                              title="Duplicate component (insert copy below)"
                            >
                              <ContentCopy />
                            </IconButton>
                            <IconButton
                              aria-label="copy to clipboard"
                              onClick={() =>
                                handleCopyTabComponent(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex
                                )
                              }
                              color="secondary"
                              size="small"
                              title="Copy to clipboard (for pasting anywhere)"
                            >
                              <ContentCopy style={{ fontSize: '16px' }} />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() =>
                                handleRemoveItemFromTab(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex
                                )
                              }
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>

                          <ComponentEditor
                            type={item.type}
                            props={item.props || {}}
                            styles={item.styles || {}}
                            onChange={(updatedProps, updatedStyles) =>
                              handleUpdateTabItem(
                                Math.min(
                                  activeTabIndex,
                                  safeContainer.tabs.length - 1
                                ),
                                itemIndex,
                                {
                                  ...item,
                                  props: updatedProps,
                                  styles: updatedStyles,
                                }
                              )
                            }
                            onDelete={() =>
                              handleRemoveItemFromTab(
                                Math.min(
                                  activeTabIndex,
                                  safeContainer.tabs.length - 1
                                ),
                                itemIndex
                              )
                            }
                            configParam={configParam}
                            handleUpload={handleUpload}
                          />

                          {/* Paste button after each tab item */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              mt: 1,
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ContentPaste />}
                              onClick={() =>
                                handlePasteTabComponent(
                                  Math.min(
                                    activeTabIndex,
                                    safeContainer.tabs.length - 1
                                  ),
                                  itemIndex + 1
                                )
                              }
                              disabled={!hasClipboardData}
                              sx={{ opacity: hasClipboardData ? 1 : 0.3 }}
                            >
                              Paste Component Here
                            </Button>
                          </Box>
                        </Box>
                      </React.Fragment>
                    ))}

                    {/* Add component buttons for current tab */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#f9f9f9',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, fontWeight: 'medium' }}
                      >
                        Add Component to "
                        {
                          safeContainer.tabs[
                            Math.min(
                              activeTabIndex,
                              safeContainer.tabs.length - 1
                            )
                          ].label
                        }
                        "
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {availableComponents.map((component) => (
                          <Button
                            key={component.type}
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleAddItemToTab(
                                Math.min(
                                  activeTabIndex,
                                  safeContainer.tabs.length - 1
                                ),
                                component.type
                              )
                            }
                          >
                            + {component.type}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Empty State */}
            {safeContainer.tabs.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <TabIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1">No tabs configured</Typography>
                <Typography variant="body2">
                  Click "Add Tab" to create your first tab
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      <Typography variant="h6">
        {safeContainer.useTabs
          ? 'Regular Container Items (Not in Tabs)'
          : 'Container Items'}
      </Typography>

      {/* Paste button at the top of container items */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentPaste />}
          onClick={() => handlePasteComponent(0)}
          disabled={!hasClipboardData}
          sx={{ opacity: hasClipboardData ? 1 : 0.3 }}
        >
          Paste Component Here
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        {safeContainer.items.map((item, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              p: 2,
              border: '1px dashed #ccc',
              borderRadius: 1,
              backgroundColor: '#f9f9f9',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => handleMoveItem(index, 'up')}
                  disabled={index === 0}
                  sx={{ mr: 0.5 }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveItem(index, 'down')}
                  disabled={index === safeContainer.items.length - 1}
                  sx={{ mr: 1 }}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle1">
                  Item {index + 1}: {item.type}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  aria-label="duplicate"
                  onClick={() => handleDuplicateComponent(index)}
                  color="primary"
                  size="small"
                  title="Duplicate component (insert copy below)"
                >
                  <ContentCopy />
                </IconButton>
                <IconButton
                  aria-label="copy to clipboard"
                  onClick={() => handleCopyComponent(index)}
                  color="secondary"
                  size="small"
                  title="Copy to clipboard (for pasting anywhere)"
                >
                  <ContentCopy style={{ fontSize: '16px' }} />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveItem(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <ComponentEditor
              type={item.type}
              props={item.props}
              styles={item.styles}
              onChange={(updatedProps, updatedStyles) => {
                handleItemChange(index, {
                  ...item,
                  props: updatedProps,
                  styles: updatedStyles,
                });
              }}
              configParam={configParam}
              handleUpload={handleUpload}
            />

            {/* Paste button after each container item */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentPaste />}
                onClick={() => handlePasteComponent(index + 1)}
                disabled={!hasClipboardData}
                sx={{ opacity: hasClipboardData ? 1 : 0.3 }}
              >
                Paste Component Here
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <Typography variant="subtitle1">Add New Item</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {availableComponents
          .filter((comp) => comp.type !== 'Container') // Prevent nested containers for simplicity
          .map((component) => (
            <Button
              key={component.type}
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddItem(component.type)}
            >
              {component.type}
            </Button>
          ))}
      </Box>
    </Box>
  );
};

export default ContainerEditor;
